/**
 * Adapters between MedicineForm's Med v2 body shape (camelCase, sent to
 * api-medicine-v2-service today) and the transcript's captured-item
 * payload shape (snake_case, matches what the Python agent emits and
 * what Go's SaveSection fan-out reads on Save & next).
 *
 * MedicineForm is now the one editor used by both the standalone
 * Medicines tab and the AI transcript modal. Same pattern as
 * complaintBodyAdapters / diagnosisBodyAdapters.
 *
 *   Med v2 add body        → captured "add" payload
 *   {medicine, formulation, medicineCode?, diagnosisId?, sinceWhen,
 *    medStartDate, medEndDate?, ongoingIndefinitely, durationDays,
 *    doseQuantity:{value,unit}, doseFrequency:{timesOfDay,
 *    customValue?, customUnit}, instructions, description}
 *      →
 *   {medicine, formulation, dose_qty, frequency, time_period,
 *    start_date, end_date, instructions, status, description}
 *
 * `medicineCode` + `diagnosisId` are Med v2-only concepts (mCode catalog
 * pick + linked diagnosis ObjectID); the transcript flow never sets
 * them (per SECTIONS_SPEC.md simplification B for the diagnosis link,
 * and because the AI doesn't have mCode).
 *
 * Wave A first-cut: only bodyToCapturedAdd is used. The update adapter
 * is stubbed for symmetry; the agent doesn't auto-route meds to update
 * in Wave A pass 1.
 */

// {value, unit} → "1 tab" / "500 mg" / "5 ml". Empty when both empty.
const doseText = (dq) => {
  const v = (dq && dq.value) || "";
  const u = (dq && dq.unit) || "";
  if (!v && !u) return "";
  return `${v} ${u}`.trim();
};

// Med v2's DoseFrequency → a human short-form the AI's `frequency`
// field naturally speaks:
//   timesOfDay: ["Morning","Night"] → "Morning, Night"
//   custom:     {value:3, unit:"Days"} → "3 Days"
// `asNeeded` slot tag → "As needed" so review-table + captured payload
// summary stays human-readable instead of surfacing the raw enum key.
const slotLabel = (s) => (s === "asNeeded" ? "As needed" : s);
const frequencyText = (df) => {
  if (!df) return "";
  if (Array.isArray(df.timesOfDay) && df.timesOfDay.length > 0) {
    return df.timesOfDay.map(slotLabel).join(", ");
  }
  if (df.customValue && df.customUnit) return `${df.customValue} ${df.customUnit}`;
  return "";
};

// derived duration → verbatim time_period the AI would say
const timePeriodText = (durationDays, ongoingIndefinitely) => {
  if (ongoingIndefinitely) return "lifelong";
  const n = Number(durationDays || 0);
  if (n <= 0) return "";
  if (n % 365 === 0) return `${n / 365} year${n === 365 ? "" : "s"}`;
  if (n % 30 === 0) return `${n / 30} month${n === 30 ? "" : "s"}`;
  if (n % 7 === 0) return `${n / 7} week${n === 7 ? "" : "s"}`;
  return `${n} day${n === 1 ? "" : "s"}`;
};

// ISO datetime → YYYY-MM-DD. Same helper as CC / Dx.
const toIsoDate = (iso) => (typeof iso === "string" && iso ? iso.slice(0, 10) : "");

// Times-of-day per-slot summary for the AI-compat `dose_qty` string when
// the reviewer used per-slot quantities. "2 tab (Morning); 1 tab (Night)"
// keeps the review-table display readable without losing the structure
// (which travels alongside as `per_slot_quantities`).
const perSlotDoseSummary = (perSlot) => {
  if (!perSlot) return "";
  const order = ["Morning", "Afternoon", "Evening", "Night"];
  const parts = [];
  order.forEach((slot) => {
    const q = perSlot[slot];
    if (!q) return;
    const t = doseText(q);
    if (!t) return;
    parts.push(`${t} (${slot})`);
  });
  return parts.join("; ");
};

/**
 * Med v2 add body → captured "add" payload. Adds structured
 * `dose_quantity` / `per_slot_quantities` /
 * `dose_frequency` blocks alongside the AI-compat text summaries so the
 * Go adapter can reconstruct the new UI's full fidelity on Save & next
 * (per-slot doses, As-needed slot in TimesOfDay, Custom-mode As-needed
 * checkbox). The text summaries stay so review tables + Python-agent
 * captures speak the same shape they always did.
 *
 * Catalog binding is preserved via `mcode` (medicine catalog PK) so
 * Save & next lands with the same identity the reviewer picked. The
 * Go transcript adapter reads `payload.mcode` and forwards it as
 * `medicineCode.mCode` on the Med v2 wire body. Manufacturer +
 * formulation name ride alongside for symmetry with the standalone
 * body — they're display snapshots the Med v2 record can carry when
 * useful.
 */
export function bodyToCapturedAdd(body) {
  const hasPerSlot = body.perSlotQuantities && Object.keys(body.perSlotQuantities).length > 0;
  const mc = body.medicineCode || null;
  return {
    medicine:         body.medicine || "",
    // Catalog binding — mCode is the medicine catalog PK. Empty when
    // no catalog row is bound (which the standalone canSubmit gate
    // now blocks; kept as a safety default for robustness).
    mcode:             (mc && mc.mCode) || "",
    formulation:      body.formulation || "",
    formulation_name:  body.formulationName || (mc && mc.formulationName) || "",
    manufacturer_name: (mc && mc.manufacturerName) || "",
    // Text summary: prefer per-slot pretty-print when it applies so the
    // review table renders "2 tab (Morning); 1 tab (Night)" instead of
    // an empty string.
    dose_qty:         hasPerSlot ? perSlotDoseSummary(body.perSlotQuantities) : doseText(body.doseQuantity),
    frequency:        frequencyText(body.doseFrequency),
    time_period:      timePeriodText(body.durationDays, body.ongoingIndefinitely),
    start_date:       toIsoDate(body.medStartDate),
    end_date:         toIsoDate(body.medEndDate),
    instructions:     body.instructions || "",
    status:           body.ongoingIndefinitely ? "ongoing" : "scheduled",
    description:      body.description || "",
    // Structured shapes — full fidelity for Save & next. Only present
    // when actually used so the payload stays small on AI captures that
    // predate the new UI.
    dose_quantity:      body.doseQuantity || undefined,
    per_slot_quantities: hasPerSlot ? body.perSlotQuantities : undefined,
    dose_frequency:     body.doseFrequency || undefined,
  };
}

/**
 * Placeholder — Wave A pass 1 doesn't have update auto-routing for meds,
 * so the update adapter is stubbed for symmetry. Wave A pass 2 fills
 * this in alongside MedicineStatusForm's transcript-mode extension.
 */
export function bodyToCapturedUpdate(body, context) {
  const ctx = context || {};
  const hasPerSlot = body.perSlotQuantities && Object.keys(body.perSlotQuantities).length > 0;
  // Context carries the original medicine identity for updates (name +
  // formulation come from the record being edited, not the form body,
  // since Update paths lock the name field). mCode threads through the
  // same channel so Save & next lands the update on the correct
  // catalog-bound row.
  const mc = ctx.medicineCode || body.medicineCode || null;
  return {
    medicine:         ctx.medicine || "",
    mcode:             (mc && mc.mCode) || ctx.mcode || "",
    formulation:      ctx.formulation || "",
    formulation_name:  ctx.formulationName || (mc && mc.formulationName) || "",
    manufacturer_name: (mc && mc.manufacturerName) || ctx.manufacturerName || "",
    dose_qty:         hasPerSlot ? perSlotDoseSummary(body.perSlotQuantities) : doseText(body.doseQuantity),
    frequency:        frequencyText(body.doseFrequency),
    time_period:      timePeriodText(body.durationDays, body.ongoingIndefinitely),
    start_date:       toIsoDate(body.medStartDate),
    end_date:         toIsoDate(body.medEndDate),
    instructions:     body.instructions || "",
    status:           body.status || "ongoing",
    description:      body.description || "",
    dose_quantity:      body.doseQuantity || undefined,
    per_slot_quantities: hasPerSlot ? body.perSlotQuantities : undefined,
    dose_frequency:     body.doseFrequency || undefined,
  };
}
